"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const RULES = [
  {
    topic: "No subscription gate",
    rule: "Adding a worker isn't limited by your plan",
  },
  {
    topic: "No name uniqueness check",
    rule: "Worker Name can repeat freely. The only thing the system guarantees is unique per worker is the auto-generated Worker Code",
  },
  {
    topic: "Department is optional",
    rule: "A worker can be saved with no Department Name at all",
  },
  {
    topic: "No active/inactive status",
    rule: "There's no deactivate action on a worker, only Edit and Delete",
  },
  {
    topic: "Deleting",
    rule: "Blocked if the worker appears anywhere on a Production Order: as its owner, in its manpower list, in a submission's manpower cost, or on a per-process assignment",
  },
  {
    topic: "Rate changes aren't retroactive",
    rule: "Editing a worker's Salary Per Hour only affects assignments made after the change. Past Production Orders keep the rate that was copied onto them at the time",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. A worker record is a name, an optional department, and an hourly rate" },
      { heading: "2. The hourly rate is copied onto each assignment, not looked up live" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p key={item.term} className="text-[16px] geist-regular text-[#374151] leading-relaxed">
        <span className="geist-medium text-[#111827]">{item.term}:</span> {item.desc}
      </p>
    ))}
  </div>
);

export default function ManPowerPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            ManPower
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone costing worker labor into production runs
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <span className="geist-medium text-[#111827]">ManPower</span>{" "}
            record is a worker register: name, department, and an hourly
            rate. Its job is to give a Production Order a rate to multiply by
            hours worked, nothing more.
          </p>
        </motion.div>

        <hr className="my-8" />

        {/* What this module does */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          {/* 1. Worker record fields */}
          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. A worker record is a name, an optional department, and an hourly rate
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The Add Worker form has exactly three fields you fill in yourself.
          </p>
          <Definitions
            items={[
              {
                term: "Worker Code",
                desc: "Assigned automatically with an MP prefix. This is the only field the system actually guarantees is unique.",
              },
              {
                term: "Worker Name",
                desc: "Required, but nothing stops two workers from having the exact same name.",
              },
              {
                term: "Department Name",
                desc: "Optional. A worker can be saved without one.",
              },
              {
                term: "Salary Per Hour",
                desc: "Required. This is the number every labor cost calculation is built from.",
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Add Worker form, showing Worker Code, Worker Name, Department Name, and Salary Per Hour." />

          {/* 2. Rate copied, not live */}
          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. The hourly rate is copied onto each assignment, not looked up live
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            A worker connects to production in three separate places, and
            all three capture the rate as it stood at that moment.
          </p>
          <Definitions
            items={[
              {
                term: "As the Production Order's owner or its manpower list",
                desc: "The worker's current Salary Per Hour is copied onto the order's own manpower line.",
              },
              {
                term: "Per process, inside a Process Route run",
                desc: "Each process step that names a worker carries its own copy of the rate for that step.",
              },
              {
                term: "On a submission's manpower cost",
                desc: "When production is submitted, the hours worked and the rate at that time are multiplied and stored as the worker's cost for that submission.",
              },
              {
                term: "Editing a worker later",
                desc: "Only changes what gets copied onto new assignments from that point on. It never rewrites a number already saved on an existing order.",
              },
            ]}
          />
          <Callout type="note">
            The Department dropdown used when assigning workers to
            production also accepts a special value that finds every worker
            with no department set, rather than filtering by a named
            department. It&apos;s a way to find undepartmented workers, not
            a hidden worker record being excluded from view.
          </Callout>
          <Screenshot caption="Screenshot: a Production Order's manpower section, showing a worker's rate carried onto the order." />
        </motion.div>

        <hr className="my-8" />

        {/* Business rules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr key={r.topic} className={i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""}>
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">{r.rule}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Related */}
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
          <Link
            href="/documentation/books/production/add-production"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Add Production <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Where a worker's rate actually gets copied onto a run
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/process"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] transition-colors mb-1 flex items-center gap-1.5">
              Process <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#6B7280]">
              Steps that can each name a worker and capture a rate
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
